import { useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMinisterSearch } from '../hooks/useUsers';
import type { MinisterOption } from '../types';

interface MinisterPickerProps {
  value: MinisterOption | null;
  onChange: (minister: MinisterOption | null) => void;
  disabled?: boolean;
}

/** Search an active minister by name, CI or ADB code */
export function MinisterPicker({ value, onChange, disabled }: MinisterPickerProps) {
  const [query, setQuery] = useState('');
  const { data: results = [], isFetching } = useMinisterSearch(query);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        <span><span className="font-medium">{value.full_name}</span> <span className="text-muted-foreground">· {value.adb_code}</span></span>
        {!disabled && (
          <Button type="button" variant="ghost" size="icon" className="size-7" aria-label="Quitar ministro" onClick={() => onChange(null)}>
            <X className="size-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Buscar por nombre, CI o código ADB"
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Buscar ministro"
        />
        {isFetching && <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>
      {query.trim().length >= 2 && !isFetching && results.length === 0 && (
        <p className="text-xs text-muted-foreground">Ningún ministro activo coincide.</p>
      )}
      {results.length > 0 && (
        <ul className="max-h-48 divide-y overflow-auto rounded-md border" role="listbox" aria-label="Resultados">
          {results.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                onClick={() => { onChange(m); setQuery(''); }}
              >
                <span className="font-medium">{m.full_name}</span>
                <span className="text-xs text-muted-foreground">{m.adb_code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
